/**
 * Workflow connection requirement helpers.
 *
 * Derives the connection requirement list shown by the workflow Connections
 * dialog from the workflow's live node→reference mapping and its resolved
 * connection references. Extracted from `ConnectionsDialog` so the derivation
 * is unit-testable and reusable independent of the dialog component.
 */

import { isManagedApiConnector } from './connector';
import type { ConnectionMapping } from '@/types/connectionReference';
import type { TemplateConnectionRequirement } from '@/types/template';

/**
 * Build the connection requirement list for the Connections dialog: one entry
 * per distinct connection **reference key** the workflow's nodes are bound to
 * (configured or not). Iterates the workflow-scoped `mapping`
 * (nodeId → referenceKey) and emits a row per unique reference key, resolving
 * each key against `references` for its connector apiId. Skips:
 *   - nodes with no mapping
 *   - duplicate reference keys (multiple nodes sharing one reference collapse
 *     into a single row)
 *   - reference keys not present in `references` or with no apiId
 *
 * The row identity is the reference key (not the connector apiId), so picking
 * a new connection on one row in the dialog only rebinds that single reference
 * — other workflow nodes sharing the same connector are untouched.
 */
export function deriveWorkflowConnectionRequirements(
  mapping: ConnectionMapping,
  references: Record<string, { api?: { id?: string } }>,
): TemplateConnectionRequirement[] {
  // Case-tolerant lookup of references — workflow JSON and ARM names can drift
  // in casing, so build a lowercased index alongside the live keys.
  const refByLowerKey = new Map<string, { key: string; apiId: string }>();
  for (const [refKey, ref] of Object.entries(references)) {
    const apiId = ref.api?.id;
    if (!apiId) continue;
    refByLowerKey.set(refKey.toLowerCase(), { key: refKey, apiId });
  }

  const seen = new Set<string>();
  const reqs: TemplateConnectionRequirement[] = [];
  for (const refKey of Object.values(mapping)) {
    if (!refKey) continue;
    const lower = refKey.toLowerCase();
    if (seen.has(lower)) continue;
    const resolved = refByLowerKey.get(lower);
    if (!resolved) continue;
    seen.add(lower);
    reqs.push({
      connectorType: isManagedApiConnector(resolved.apiId) ? 'shared' : 'inapp',
      apiId: resolved.apiId,
      referenceKey: resolved.key,
    });
  }
  return reqs;
}
